/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { AssociationEntry } from '../model/associationEntry';
import { ChildAssociationEntry } from '../model/childAssociationEntry';
import { DirectAccessUrlEntry } from '../model/directAccessUrlEntry';
import { NodeAssociationPaging } from '../model/nodeAssociationPaging';
import { NodeChildAssociationPaging } from '../model/nodeChildAssociationPaging';
import { NodeEntry } from '../model/nodeEntry';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class NodesApi extends BaseApi {
    copyNode(nodeId, nodeBodyCopy, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyCopy, 'nodeBodyCopy');
        opts = opts || {};
        const postBody = nodeBodyCopy;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/copy', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    createAssociation(nodeId, associationBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(associationBodyCreate, 'associationBodyCreate');
        opts = opts || {};
        const postBody = associationBodyCreate;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/targets', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, AssociationEntry);
    }
    createNode(nodeId, nodeBodyCreate, opts, formParams) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyCreate, 'nodeBodyCreate');
        opts = opts || {};
        let postBody = nodeBodyCreate;
        let pathParams = {
            'nodeId': nodeId
        };
        let queryParams = {
            'autoRename': opts['autoRename'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        let headerParams = {};
        formParams = formParams || {};
        let contentTypes, returnType = null;
        if (formParams.filedata) {
            contentTypes = ['multipart/form-data'];
        }
        else {
            contentTypes = ['application/json'];
            returnType = NodeEntry;
        }
        let accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/children', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, returnType);
    }
    createSecondaryChildAssociation(nodeId, secondaryChildAssociationBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(secondaryChildAssociationBodyCreate, 'secondaryChildAssociationBodyCreate');
        opts = opts || {};
        const postBody = secondaryChildAssociationBodyCreate;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/secondary-children', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, ChildAssociationEntry);
    }
    deleteAssociation(nodeId, targetId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(targetId, 'targetId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId, 'targetId': targetId
        };
        const queryParams = {
            'assocType': opts['assocType']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/targets/{targetId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    deleteNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'permanent': opts['permanent']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    deleteSecondaryChildAssociation(nodeId, childId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(childId, 'childId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId, 'childId': childId
        };
        const queryParams = {
            'assocType': opts['assocType']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/secondary-children/{childId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    getNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'relativePath': opts['relativePath'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    getNodeContent(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'attachment': opts['attachment']
        };
        const headerParams = {
            'If-Modified-Since': opts['ifModifiedSince'], 'Range': opts['range']
        };
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/octet-stream'];
        return this.apiClient.callApi('/nodes/{nodeId}/content', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, 'blob');
    }
    listNodeChildren(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'orderBy': buildCollectionParam(opts['orderBy'], 'csv'),
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'relativePath': opts['relativePath'],
            'includeSource': opts['includeSource'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/children', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeChildAssociationPaging);
    }
    listParents(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'includeSource': opts['includeSource'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/parents', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeAssociationPaging);
    }
    listSecondaryChildren(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'includeSource': opts['includeSource'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/secondary-children', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeChildAssociationPaging);
    }
    listSourceAssociations(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/sources', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeAssociationPaging);
    }
    listTargetAssociations(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/targets', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeAssociationPaging);
    }
    lockNode(nodeId, nodeBodyLock, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyLock, 'nodeBodyLock');
        opts = opts || {};
        const postBody = nodeBodyLock;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/lock', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    moveNode(nodeId, nodeBodyMove, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyMove, 'nodeBodyMove');
        opts = opts || {};
        const postBody = nodeBodyMove;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/move', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    unlockNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/unlock', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    updateNode(nodeId, nodeBodyUpdate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyUpdate, 'nodeBodyUpdate');
        opts = opts || {};
        const postBody = nodeBodyUpdate;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    updateNodeContent(nodeId, contentBodyUpdate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(contentBodyUpdate, 'contentBodyUpdate');
        opts = opts || {};
        const postBody = contentBodyUpdate;
        const pathParams = {
            'nodeId': nodeId
        };
        const queryParams = {
            'majorVersion': opts['majorVersion'],
            'comment': opts['comment'],
            'name': opts['name'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/octet-stream'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/content', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, NodeEntry);
    }
    requestDirectAccessUrl(nodeId) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            'nodeId': nodeId
        };
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/nodes/{nodeId}/request-direct-access-url', 'POST', pathParams, {}, {}, {}, null, contentTypes, accepts, DirectAccessUrlEntry);
    }
}
